import { AbstractMesh, Color3, Color4, Matrix, MeshBuilder, MeshParticleEmitter, ParticleSystem, Vector3, type LinesMesh, type Node, type Scene, type TransformNode } from '@babylonjs/core';
import { EssenceThread } from './essence-thread';
import { applyParticleSprite, ESSENCE_GLINT_LOOK } from './particle-sprites';

/** Where a bow's string ties on: the `nock-top` and `nock-bottom` nodes `fit-autosprite-bow.mts` puts at its limb tips. */
export interface BowNocks {
    readonly top: TransformNode;
    readonly bottom: TransformNode;
}

/** The string's color: dark, waxed sinew. */
const STRING_COLOR = new Color3(0.12, 0.1, 0.09);
/** The furthest the nocking point is drawn back from the string at rest, in meters. */
const MAX_DRAW_METERS = 0.8;
/**
 * Let go, the string is home almost at once, then overshoots a little and
 * settles: it returns over this long, then quivers forward no further than
 * the recoil (a share of the draw, and never more than a few centimeters)
 * and is still after the settle.
 */
const SNAP_RETURN_SECONDS = 0.03;
const SNAP_SETTLE_SECONDS = 0.12;
const SNAP_RECOIL_SHARE = 0.2;
const SNAP_MAX_RECOIL_METERS = 0.06;
const SNAP_HZ = 20;
const SNAP_DAMPING = 30;

/** How a string is drawn: dark sinew, or a thread of essence in its color. */
export type BowStringLook = { readonly kind: 'sinew' } | { readonly kind: 'essence'; readonly colorHex: string };

/** An essence string: the thread's width, and the arrow of essence formed on it through the draw (its width, and how far its tip leads past the grip). */
const ESSENCE_STRING_WIDTH = 0.06;
const ESSENCE_ARROW_WIDTH = 0.09;
const ESSENCE_ARROW_LEAD_METERS = 0.15;
/** An essence bow's shimmer: glints of its essence rising off its limbs. */
const SHIMMER_RATE = 14;

/** A bow's nocks under `root` (an attached bow's root), or null for anything that is not a bow. */
export function findBowNocks(root: Node): BowNocks | null {
    // A model shown more than once is cloned, which can prefix its node names.
    const named = (suffix: string): TransformNode | undefined => root.getDescendants(false, (node) => node.name.endsWith(suffix))[0] as TransformNode | undefined;
    const top = named('nock-top');
    const bottom = named('nock-bottom');
    return top && bottom ? { top, bottom } : null;
}

/**
 * A bow's string, drawn at runtime so it can be pulled: a line from one
 * nock to the other through the nocking point. It is in one of three
 * states: at rest, the nocking point is the middle of the nocks; held, it
 * is tied to the drawing hand (`Palm.R`); let go, it is home almost at
 * once, with a small forward recoil that dies out fast.
 *
 * A sinew string is a dark line; an essence string a thread of its essence,
 * with an arrow of it formed on the string through the draw and the bow
 * shimmering with it. Either is parented to the bow and laid out in its space, so it moves
 * with the bow exactly, however the bow is animated: the nocks are fixed
 * points of it, worked out once, and only the drawing hand is brought into
 * its space each frame.
 */
export class BowString {
    /** A sinew string's line; an essence string has threads instead. */
    private readonly line: LinesMesh | null = null;
    private readonly essence: {
        readonly string: EssenceThread;
        /** The arrow of essence lying on the string while it is drawn, from the hand to just past the grip. */
        readonly arrow: EssenceThread;
        readonly shimmer: ParticleSystem | null;
    } | null = null;
    private readonly top: Vector3;
    private readonly bottom: Vector3;
    private readonly rest: Vector3;
    private readonly points: [Vector3, Vector3, Vector3];
    private readonly toBow = new Matrix();
    /** Whether the drawing hand holds it now. */
    private isHeld = false;
    /** Shown at all (`setVisible`): the arrow shows only while held and shown. */
    private isVisible = true;
    /** Snapping back since it was let go: how far from rest it was, and how long ago (seconds). */
    private snap: { readonly offset: Vector3; seconds: number } | null = null;
    private lastUpdateMs: number | null = null;

    constructor(private readonly scene: Scene, name: string, private readonly bow: TransformNode, nocks: BowNocks, look: BowStringLook = { kind: 'sinew' }) {
        // The nocks in the bow's own space: rigid children of it, so worked out once.
        const topWorld = worldPositionUnder(bow, nocks.top);
        const bottomWorld = worldPositionUnder(bow, nocks.bottom);
        bow.getWorldMatrix().invertToRef(this.toBow);
        this.top = Vector3.TransformCoordinates(topWorld, this.toBow);
        this.bottom = Vector3.TransformCoordinates(bottomWorld, this.toBow);
        this.rest = Vector3.Lerp(this.top, this.bottom, 0.5);
        this.points = [this.top.clone(), this.rest.clone(), this.bottom.clone()];
        if (look.kind === 'essence') {
            const string = new EssenceThread(`${name}-thread`, scene, { parent: bow, colorHex: look.colorHex, width: ESSENCE_STRING_WIDTH, bend: 0, strength: 0.4 });
            const arrow = new EssenceThread(`${name}-arrow`, scene, { parent: bow, colorHex: look.colorHex, width: ESSENCE_ARROW_WIDTH, bend: 0, strength: 0.9, seed: 0.5 });
            arrow.mesh.setEnabled(false);
            this.essence = { string, arrow, shimmer: this.createShimmer(`${name}-shimmer`, look.colorHex) };
        } else {
            this.line = MeshBuilder.CreateLines(name, { points: this.points, updatable: true }, scene);
            this.line.color = STRING_COLOR;
            this.line.isPickable = false;
            this.line.parent = bow;
        }
    }

    /**
     * Ties the string to `hand` (the drawing hand) while it holds it, no
     * further back than a full draw; with none, a string just let go snaps
     * back and one at rest stays put. `nowMs` times the snap.
     */
    update(hand: TransformNode | null, nowMs: number): void {
        const deltaSeconds = this.lastUpdateMs === null ? 0 : Math.max(0, (nowMs - this.lastUpdateMs) / 1000);
        this.lastUpdateMs = nowMs;
        const middle = this.points[1];
        if (hand) {
            this.isHeld = true;
            this.snap = null;
            this.bow.getWorldMatrix().invertToRef(this.toBow);
            Vector3.TransformCoordinatesToRef(hand.getAbsolutePosition(), this.toBow, middle);
            // Bow space is meters only at unit scale; measure the draw in scene space.
            const drawn = middle.subtract(this.rest);
            const scale = this.bow.absoluteScaling.x || 1;
            const length = drawn.length() * scale;
            if (length > MAX_DRAW_METERS) {
                this.rest.addToRef(drawn.scaleInPlace(MAX_DRAW_METERS / length), middle);
            }
        } else if (this.isHeld) {
            // Let go: it springs back from where the hand left it.
            this.isHeld = false;
            this.snap = { offset: middle.subtract(this.rest), seconds: 0 };
        }
        if (this.snap) {
            this.snap.seconds += deltaSeconds;
            const t = this.snap.seconds;
            const { offset } = this.snap;
            if (t < SNAP_RETURN_SECONDS) {
                // Home almost at once, from where the hand let go.
                this.rest.addToRef(offset.scale(1 - t / SNAP_RETURN_SECONDS), middle);
            } else if (t < SNAP_RETURN_SECONDS + SNAP_SETTLE_SECONDS) {
                // A small forward recoil, past rest the other way, dying out fast.
                const u = t - SNAP_RETURN_SECONDS;
                const scale = this.bow.absoluteScaling.x || 1;
                const drawLength = offset.length();
                const recoil = Math.min(drawLength * SNAP_RECOIL_SHARE, SNAP_MAX_RECOIL_METERS / scale);
                const wave = Math.sin(2 * Math.PI * SNAP_HZ * u) * Math.exp(-SNAP_DAMPING * u);
                this.rest.addToRef(offset.scale(drawLength > 0 ? (-recoil * wave) / drawLength : 0), middle);
            } else {
                this.snap = null;
                middle.copyFrom(this.rest);
            }
        } else if (!this.isHeld) {
            middle.copyFrom(this.rest);
        }
        if (this.line) {
            MeshBuilder.CreateLines(this.line.name, { points: this.points, instance: this.line }, this.scene);
        }
        if (this.essence) {
            this.updateEssence(deltaSeconds);
        }
    }

    /** The essence string along its points, and while drawn the arrow of essence on it, brighter the further it is drawn. */
    private updateEssence(deltaSeconds: number): void {
        const { string, arrow } = this.essence!;
        string.setPath(this.points);
        string.update(deltaSeconds);
        arrow.mesh.setEnabled(this.isHeld && this.isVisible);
        if (!this.isHeld) {
            return;
        }
        const middle = this.points[1];
        // The grip is the bow's origin (`fit-autosprite-bow.mts`): the arrow lies from the hand across it.
        const across = Vector3.Zero().subtract(middle);
        const reach = across.length();
        if (reach < 1e-4) {
            return;
        }
        const scale = this.bow.absoluteScaling.x || 1;
        const tip = across.scale((reach + ESSENCE_ARROW_LEAD_METERS / scale) / reach).addInPlace(middle);
        arrow.setPath([middle.clone(), tip]);
        arrow.setIntensity(0.5 + 0.5 * Math.min(1, middle.subtract(this.rest).length() * scale / MAX_DRAW_METERS));
        arrow.update(deltaSeconds);
    }

    /** Glints of essence rising off the bow's limbs, emitted from its surface; none for a bow with no mesh. */
    private createShimmer(name: string, colorHex: string): ParticleSystem | null {
        const mesh = this.bow.getChildMeshes(false).find((child): child is AbstractMesh => child instanceof AbstractMesh && child.getTotalVertices() > 0);
        if (!mesh) {
            return null;
        }
        const color = Color3.FromHexString(colorHex);
        const shimmer = new ParticleSystem(name, 60, this.scene);
        applyParticleSprite(shimmer, this.scene, ESSENCE_GLINT_LOOK);
        shimmer.particleEmitterType = new MeshParticleEmitter(mesh);
        shimmer.emitter = mesh;
        shimmer.emitRate = SHIMMER_RATE;
        shimmer.minLifeTime = 0.4;
        shimmer.maxLifeTime = 0.9;
        shimmer.minSize = 0.03;
        shimmer.maxSize = 0.07;
        shimmer.minEmitPower = 0.05;
        shimmer.maxEmitPower = 0.2;
        shimmer.gravity = new Vector3(0, 0.25, 0);
        shimmer.color1 = new Color4(color.r, color.g, color.b, 1);
        shimmer.color2 = new Color4(1, 1, 1, 0.9);
        shimmer.colorDead = new Color4(color.r, color.g, color.b, 0);
        shimmer.blendMode = ParticleSystem.BLENDMODE_ADD;
        shimmer.start();
        return shimmer;
    }

    setVisible(visible: boolean): void {
        this.isVisible = visible;
        this.line?.setEnabled(visible);
        if (this.essence) {
            this.essence.string.mesh.setEnabled(visible);
            this.essence.arrow.mesh.setEnabled(visible && this.isHeld);
            if (this.essence.shimmer) {
                this.essence.shimmer.emitRate = visible ? SHIMMER_RATE : 0;
            }
        }
    }

    dispose(): void {
        this.line?.dispose();
        this.essence?.string.dispose();
        this.essence?.arrow.dispose();
        // The glint sprite is shared, so it is kept.
        this.essence?.shimmer?.dispose(false);
    }
}

/**
 * Where `node` is now, in scene space: each node from `bow` down to it
 * recomputed in turn, so none is read against a parent's stale matrix.
 */
function worldPositionUnder(bow: TransformNode, node: TransformNode): Vector3 {
    const path: TransformNode[] = [];
    for (let current: Node | null = node; current && current !== bow; current = current.parent) {
        path.unshift(current as TransformNode);
    }
    bow.computeWorldMatrix(true);
    path.forEach((step) => step.computeWorldMatrix(true));
    return node.getAbsolutePosition().clone();
}
